import { env } from '@/lib/env'

/**
 * THE RENEWALS CLIENT (Kelvo pre-pilot item 3, batch 2y).
 *
 * Talks to the owner-only /api/admin/renewals routes (workers/api.ts) and
 * holds every word the Renewals page shows about a date, so the wording is
 * tested in one place. React-free, like src/lib/api.ts.
 *
 * NEVER TRUST THE WIRE. Each row is coerced field by field; a row with no id
 * or no real day is dropped rather than drawn wrong.
 */

export type RenewalBand = 'overdue' | 'soon' | 'later'

export interface Renewal {
  id: string
  title: string
  holder: string | null
  document: { id: string; name: string } | null
  dueOn: string
  band: RenewalBand
  daysLeft: number
}

export interface RenewalsList {
  today: string
  due: number
  renewals: Renewal[]
}

export interface NewRenewalInput {
  title: string
  holder?: string
  documentId?: string
  dueOn: string
}

export class RenewalsError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'RenewalsError'
  }
}

/**
 * The same rule the server and the database enforce, checked as he types, so
 * he is told before he presses Add. The server remains the authority.
 */
export function numberProblem(title: string, holder: string): string | null {
  if (/\d{6}/.test(title)) return 'Leave out card, policy and certificate numbers. Kelvo only needs what it is and the date.'
  if (/\d/.test(holder)) return 'Just the person’s name, with no numbers.'
  return null
}

const DAY = /^\d{4}-\d{2}-\d{2}$/

function toRenewal(raw: unknown): Renewal | null {
  const r = (raw ?? {}) as Record<string, unknown>
  if (typeof r.id !== 'string' || !r.id) return null
  if (typeof r.dueOn !== 'string' || !DAY.test(r.dueOn)) return null
  const band: RenewalBand = r.band === 'overdue' || r.band === 'soon' ? r.band : 'later'
  const doc = (r.document ?? null) as Record<string, unknown> | null
  return {
    id: r.id,
    title: typeof r.title === 'string' ? r.title : '',
    holder: typeof r.holder === 'string' && r.holder ? r.holder : null,
    document:
      doc && typeof doc.id === 'string' && typeof doc.name === 'string' ? { id: doc.id, name: doc.name } : null,
    dueOn: r.dueOn,
    band,
    daysLeft: typeof r.daysLeft === 'number' && Number.isFinite(r.daysLeft) ? r.daysLeft : 0,
  }
}

async function request(
  token: string | null,
  path: string,
  init: { method: string; body?: unknown },
): Promise<unknown> {
  if (!token) throw new RenewalsError('Your sign-in has expired. Please sign in again.', 401)
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}${path}`, {
      method: init.method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    })
  } catch {
    throw new RenewalsError('No connection. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new RenewalsError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message = (data as { message?: unknown } | null)?.message
    throw new RenewalsError(typeof message === 'string' ? message : 'Something went wrong. Please try again.', res.status)
  }
  return data
}

export async function fetchRenewals(token: string | null): Promise<RenewalsList> {
  const data = (await request(token, '/api/admin/renewals', { method: 'GET' })) as Record<string, unknown> | null
  const list = data?.renewals
  if (!Array.isArray(list) || typeof data?.today !== 'string') {
    throw new RenewalsError('Unexpected response from the engine.')
  }
  const renewals = list.map(toRenewal).filter((r): r is Renewal => r !== null)
  return {
    today: data.today,
    due: renewals.filter((r) => r.band !== 'later').length,
    renewals,
  }
}

export async function addRenewal(token: string | null, input: NewRenewalInput): Promise<string> {
  const body: Record<string, string> = { title: input.title, dueOn: input.dueOn }
  if (input.holder && input.holder.trim()) body.holder = input.holder
  if (input.documentId) body.documentId = input.documentId
  const data = (await request(token, '/api/admin/renewals', { method: 'POST', body })) as { id?: unknown } | null
  if (typeof data?.id !== 'string') throw new RenewalsError('Unexpected response from the engine.')
  return data.id
}

export async function renewRenewal(token: string | null, id: string, dueOn: string): Promise<void> {
  await request(token, `/api/admin/renewals/${encodeURIComponent(id)}/renew`, { method: 'POST', body: { dueOn } })
}

export async function removeRenewal(token: string | null, id: string): Promise<void> {
  await request(token, `/api/admin/renewals/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// --- Words --------------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** '2027-03-31' → '31 Mar 2027'. A calendar day, so no time zone is involved. */
export function formatDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return `${d} ${MONTHS[m - 1] ?? ''} ${y}`
}

/**
 * The day after a calendar day: '2026-02-28' → '2026-03-01', '2028-02-28' →
 * '2028-02-29', '2026-12-31' → '2027-01-01'. Worked in UTC on the day number,
 * so no time zone or clock change can shift it.
 */
export function dayAfter(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10)
}

/**
 * Renewing moves the date FORWARD (the server refuses anything else, G50).
 * Checked as he picks, so an unchanged or earlier date is stopped on the page
 * with the server's own words, not sent and refused. Days compare as text
 * because YYYY-MM-DD sorts in date order. An empty box is not a problem yet:
 * the Save button waits for a date.
 */
export function renewDateProblem(newDate: string, currentDueOn: string): string | null {
  if (!newDate) return null
  if (!DAY.test(newDate)) return 'Give the renewal date as a real day.'
  if (newDate <= currentDueOn) return 'The new date must be after the current one.'
  return null
}

/** How a line's date reads on the page. */
export function dueWords(r: Pick<Renewal, 'daysLeft' | 'dueOn'>): string {
  if (r.daysLeft < -1) return `${-r.daysLeft} days overdue (${formatDay(r.dueOn)})`
  if (r.daysLeft === -1) return `1 day overdue (${formatDay(r.dueOn)})`
  if (r.daysLeft === 0) return 'Due today'
  if (r.daysLeft === 1) return 'Due tomorrow'
  if (r.daysLeft <= 30) return `Due in ${r.daysLeft} days (${formatDay(r.dueOn)})`
  return `Due ${formatDay(r.dueOn)}`
}

/** The three groups, in the order the page shows them. Empty groups are left out. */
export function groupRenewals(list: readonly Renewal[]): Array<{ band: RenewalBand; heading: string; items: Renewal[] }> {
  const headings: Record<RenewalBand, string> = {
    overdue: 'Overdue',
    soon: 'Due in the next 30 days',
    later: 'Later',
  }
  return (['overdue', 'soon', 'later'] as const)
    .map((band) => ({ band, heading: headings[band], items: list.filter((r) => r.band === band) }))
    .filter((g) => g.items.length > 0)
}

/** The header link's label: a count only when something needs attention. */
export function renewalsLinkLabel(due: number | null): string {
  return due ? `Renewals (${due})` : 'Renewals'
}
